<div class="sav-site-container">
  <app-breadcrumb [items]="crumbs()" />
</div>

<!--
  The category banner, and only when an admin actually uploaded one.

  The fallback that used to sit here was a ZAFUL swimwear advert, hotlinked from Amazon's CDN, shown
  on every category with no banner of its own — a lingerie photograph headed the Photography page.
  Three separate problems in one tag: another retailer's advertising presented as ours, an image
  served from a third party we do not control and cannot cache or resize, and no relationship
  whatsoever to the page it sat on.

  No banner is the right answer. The page below it opens with its own heading either way.
-->
@if (heroBannerUrl()) {
  <section class="sav-store-banner" aria-label="Category banner">
    <div class="sav-store-banner-surface">
      <img [src]="heroBannerUrl()" [alt]="heroAltText()" class="sav-store-banner-image" />
    </div>
  </section>
}

<!-- Editorial images: admin-managed lookbook when configured, else the static collection grid. -->
@if (editorial(); as ed) {
  <section class="sav-collection-grid" aria-label="Editorial collection">
    <div class="sav-site-container">
      @if (ed.fullHeading || ed.collageHeading) {
        <header class="sav-collection-grid-head">
          <div>
            <!-- The page's H1. The category-name block that used to sit above the banner is gone,
                 so this collection heading is the page title. Its size is unchanged. -->
            <h1>{{ ed.fullHeading || ed.collageHeading }}</h1>
            <!-- The editorial block has no subtitle field in admin, so this is built from the
                 category itself — that way it is right on every category rather than being one
                 fixed line. -->
            <p>Curated edits across {{ categoryName() }}</p>
          </div>
        </header>
      }
      <div class="sav-collection-grid-layout">
        @if (ed.full) {
          <article class="sav-collection-grid-hero">
            <img
              [src]="ed.full.imageUrl"
              [alt]="categoryName() + ' collection'"
              class="sav-collection-grid-image"
            />
            <div class="sav-collection-grid-content sav-collection-grid-content-hero">
              <!-- The category name, not `fullHeading`: that is the section's own heading just
                   above, so printing it here too put the same words on the page twice — as the H1
                   and again as this tile's caption. A caption labels the picture. -->
              <h2>{{ categoryName() }}</h2>
              @if (ed.full.url) {
                <a [href]="ed.full.url" target="_blank" rel="nofollow sponsored noopener"
                  >Shop Now</a
                >
              }
            </div>
          </article>
        }
        <div class="sav-collection-grid-stack">
          @if (ed.half) {
            <article class="sav-collection-grid-tile">
              <img
                [src]="ed.half.imageUrl"
                [alt]="categoryName()"
                class="sav-collection-grid-image"
              />
              <div class="sav-collection-grid-content">
                @if (ed.half.url) {
                  <a [href]="ed.half.url" target="_blank" rel="nofollow sponsored noopener"
                    >Shop Now</a
                  >
                }
              </div>
            </article>
          }
          @for (tile of ed.collage; track tile.imageUrl) {
            <article class="sav-collection-grid-tile">
              <img [src]="tile.imageUrl" [alt]="categoryName()" class="sav-collection-grid-image" />
              <div class="sav-collection-grid-content">
                @if (tile.url) {
                  <a [href]="tile.url" target="_blank" rel="nofollow sponsored noopener"
                    >Shop Now</a
                  >
                }
              </div>
            </article>
          }
        </div>
      </div>
    </div>
  </section>
} @else {
  <!--
    No editorial images configured for this category.

    This branch used to render a hardcoded swimwear lookbook: an H1 reading "Shop By Swim Style"
    and nine `href="#"` tiles for bikinis and cover-ups, on EVERY category that had no images —
    skincare, electronics, home and garden alike. It was placeholder markup that shipped, and it
    carried the page's H1, so the single most important heading on a skincare page said swimwear.

    What replaces it is the one thing that is always true and always correct: the category itself.
    The H1 has to live somewhere, and this branch is where it lives when there is no lookbook.
  -->
  <section class="sav-collection-grid" aria-label="Category heading">
    <div class="sav-site-container">
      <header class="sav-collection-grid-head">
        <div>
          <h1>{{ categoryName() }} Deals</h1>
          <p>Discounted {{ categoryName() }} products from the stores we track.</p>
        </div>
      </header>
    </div>
  </section>
}

<!-- Logo strip for the stores that actually carry this category, each linking to its deals page.
     Hidden when none of them have a logo, rather than showing placeholder brands.

     Sits directly under the editorial images and above the product strips: it names who stocks this
     category before the page starts listing what they stock. Below the editorial block on purpose —
     that block carries the page's H1, so this H2 must not come before it. -->
@if (topStores().length > 0) {
  <section class="sav-top-stores-section" aria-label="Top stores in this category">
    <div class="sav-top-stores-wrap">
      <h2>{{ categoryName() }} Top Stores</h2>
      <p>The retailers carrying the most products in this category.</p>

      <div class="sav-top-store-list" appAutoScrollRow>
        @for (store of topStores(); track store.path) {
          <!-- A store with no collection has no products page to link to now that `/deals/<store>`
               is retired, so it is left out rather than linked to a 410. -->
          @if (store.path) {
            <a [routerLink]="store.path" [attr.aria-label]="store.label" class="sav-top-store">
              <app-store-logo
                class="sav-top-store-logo"
                [logoUrl]="store.logoUrl"
                [name]="store.label"
                decorative
              />
            </a>
          }
        }
      </div>
    </div>
  </section>
}

<!-- A stack of product strips, mirroring the deals-store page: recommended discounts, new
     arrivals, then one section per store carrying this category. Each pages independently.
     A section only renders once it actually has products — an empty one is hidden rather than
     showing an empty heading. Sections that haven't loaded yet still emit the small placeholder
     below, because that is the element the lazy-load observer watches. -->
@for (section of sections(); track section.id) {
  @if (section.products.length > 0) {
    <section class="sav-essentials" [attr.aria-label]="section.heading">
      <div class="sav-site-container">
        <header class="sav-essentials-head">
          <div>
            <h2>{{ section.heading }}</h2>
            <p>Showing {{ section.products.length }} of {{ section.total }} products</p>
          </div>
        </header>

        <div class="sav-product-grid">
          @for (product of section.products; track product.title + $index) {
            <app-product-card
              [title]="product.title"
              [image]="product.image"
              [storeName]="product.storeName"
              [storeLogoUrl]="product.storeLogoUrl"
              [price]="product.price"
              [oldPrice]="product.oldPrice"
              [external]="product.isExternal"
              [detailHref]="product.detailPath"
              [productId]="product.id"
            />
          }
        </div>

        @if (hasMore(section)) {
          <div class="sav-essentials-more">
            <button type="button" (click)="loadMoreProducts(section)" [disabled]="section.loading">
              {{ section.loading ? 'Loading…' : 'Load more products' }}
            </button>
          </div>
        }
      </div>
    </section>
  } @else if (!section.loaded) {
    <div
      class="sav-essentials-placeholder"
      aria-hidden="true"
      [attr.data-lazy-section]="!section.eager ? section.id : null"
    ></div>
  }
}

<!--
  FAQs, admin-managed only.

  The fallback that used to sit here was four hardcoded ZAFUL questions — "How do I use a Zaful
  coupon code?", "Everything shoppers ask before using Zaful offers" — rendered on every category
  with no FAQs of its own, skincare and electronics included. No section at all is the honest
  answer; a wrong shop's FAQ is worse than none.
-->
@if (faqs().length) {
  <section class="sav-faq" aria-label="Frequently asked questions">
    <div class="sav-site-container">
      <header class="sav-faq-head">
        <h2>{{ categoryName() }} FAQs</h2>
        <p>Everything shoppers ask before using these offers.</p>
      </header>
      <div class="sav-store-faq-list">
        @for (faq of faqs(); track faq.question; let i = $index) {
          <details class="sav-store-faq-item" [open]="i === 0">
            <summary>{{ faq.question }}</summary>
            <!-- Admin FAQ answers are rich text; interpolating them printed the markup as text. -->
            <div class="sav-store-faq-answer" [innerHTML]="faq.answer | trustedCmsHtml"></div>
          </details>
        }
      </div>
    </div>
  </section>
}

<!-- Real stores carrying this category, each linking to its deals page. Hidden entirely when the
     category has none, rather than showing placeholder brands that sell nothing in it. -->
@if (hasBrands()) {
  <section class="sav-brand-links-section" aria-label="Popular stores">
    <div class="sav-site-container">
      <header class="sav-brand-links-section-head">
        <div>
          <h2>More {{ categoryName() }} Brands to Explore</h2>
          <p>Stores with products in this category</p>
        </div>
      </header>

      <div class="sav-brand-links-grid">
        @for (column of brandColumns(); track $index) {
          <div class="sav-brand-links-column">
            @for (brand of column; track brand.path) {
              <a [routerLink]="brand.path">{{ brand.label }}</a>
            }
          </div>
        }
      </div>
    </div>
  </section>
}

<!--
  Long-form guide, admin-managed only.

  The fallback that used to sit here was a hardcoded ZAFUL handbook — an H2 reading "The Complete
  Zaful Savings Guide" and several paragraphs of Zaful-specific copy — rendered on every category
  with no guide of its own. On /collection/skincare it told shoppers about Zaful. Same call as the
  FAQ block above: no section beats another shop's copy.
-->
@if (longContent()) {
  <section class="sav-longform" aria-label="Category savings guide">
    <div class="sav-site-container">
      <header class="sav-longform-head">
        <h2>{{ longHeading() || categoryName() + ' Savings Guide' }}</h2>
        @if (longIntro()) {
          <p>{{ longIntro() }}</p>
        }
      </header>
      <div
        class="sav-longform-preview sav-longform-cms"
        [innerHTML]="longContent() | trustedCmsHtml"
      ></div>
    </div>
  </section>
}
